import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import { createBrowserRouter } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { useGetEnv } from '@/api/util/useGetEnv.util.ts';
import { QueryClientWrapper } from '@/config/QueryClientWrapper.comp.tsx';
import { knowMeTheme } from '@/config/theme/theme.ts';
import { ErrorBoundary } from '@/errorHandling/ErrorBoundary.comp.tsx';
import { APP_ROUTES } from '@/routes/AppRoutes.util.tsx';
import { MaintenanceView } from '@/views/maintenance/Maintenance.view.tsx';

dayjs.extend(updateLocale);

dayjs.updateLocale('en', {
  weekStart: 0,
});
dayjs.extend(utc);

const nonce = document.querySelector("meta[property='csp-nonce']")?.getAttribute('content') ?? '';

const cache = createCache({
  key: 'css',
  nonce,
  prepend: true,
});

const router = createBrowserRouter(APP_ROUTES);

const AppContent = () => {
  const { env, isLoading } = useGetEnv();

  if (isLoading) {
    return <CssBaseline />;
  }

  if (env?.maintenanceMode) {
    return (
      <>
        <CssBaseline />
        <MaintenanceView />
      </>
    );
  }

  return <RouterProvider router={router} />;
};

function App() {
  return (
    <CacheProvider value={cache}>
      <LocalizationProvider adapterLocale="en" dateAdapter={AdapterDayjs}>
        <ThemeProvider theme={knowMeTheme}>
          <ErrorBoundary>
            <QueryClientWrapper>
              <AppContent />
            </QueryClientWrapper>
          </ErrorBoundary>
        </ThemeProvider>
      </LocalizationProvider>
    </CacheProvider>
  );
}

export default App;
